import { useEffect, useRef } from 'react';

/**
 * useBidirectionalScroll
 *
 * Attaches an IntersectionObserver to all `.animate-hidden` elements
 * inside `containerSelector`. Tracks scroll direction and sets a
 * `data-scroll-dir` attribute so CSS can mirror the origin direction.
 *
 * @param {string} containerSelector  - e.g. '.about-page-container'
 * @param {number} [delay=100]        - ms before observing (let DOM settle)
 *
 * Usage:
 *   useBidirectionalScroll('.about-page-container');
 */
export default function useBidirectionalScroll(containerSelector, delay = 100) {
  const lastScrollY = useRef(window.scrollY);

  useEffect(() => {
    const handleScroll = () => {
      lastScrollY.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const elasticTextNodes = (el) => {
      if (el.getAttribute('data-text-split') === 'true') return;
      el.setAttribute('data-text-split', 'true');

      // Helper: split text into animated character spans
      const buildSplitSpans = (text, startIdx) => {
        let charIdx = startIdx;
        const frag = document.createDocumentFragment();

        // Use Intl.Segmenter for safe grapheme splitting (Gujarati, Hindi, etc.)
        let chars;
        if (typeof Intl !== 'undefined' && Intl.Segmenter) {
          const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
          chars = Array.from(segmenter.segment(text)).map(s => s.segment);
        } else {
          chars = [...text];
        }

        chars.forEach((char) => {
          const span = document.createElement('span');
          span.textContent = char === ' ' ? '\u00A0' : char;
          span.style.display = 'inline-block';
          span.style.opacity = '0';
          span.style.transform = 'translateY(-60px)';
          span.style.transition = 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.5s ease';
          span.style.transitionDelay = `${charIdx * 25}ms`;
          frag.appendChild(span);
          charIdx++;
        });
        return { frag, endIdx: charIdx };
      };

      // Walk only TEXT NODES — preserve child elements (icons, divs, etc.)
      let globalCharIdx = 0;
      const textNodes = [];
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
      let node;
      while ((node = walker.nextNode())) {
        if (node.textContent.trim()) textNodes.push(node);
      }

      if (textNodes.length === 0) return;

      textNodes.forEach((textNode) => {
        const originalText = textNode.textContent;
        const parent = textNode.parentNode;

        // 1. Hidden span Google Translate CAN translate
        const hiddenSpan = document.createElement('span');
        hiddenSpan.className = 'sr-only';
        hiddenSpan.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);pointer-events:none;';
        hiddenSpan.textContent = originalText;
        parent.insertBefore(hiddenSpan, textNode);

        // 2. Wrapper for split chars, marked notranslate
        const wrapper = document.createElement('span');
        wrapper.classList.add('notranslate');
        wrapper.setAttribute('translate', 'no');
        wrapper.style.display = 'inline';

        const { frag, endIdx } = buildSplitSpans(originalText, globalCharIdx);
        globalCharIdx = endIdx;
        wrapper.appendChild(frag);
        wrapper.setAttribute('data-last-text', originalText);

        // 3. Replace the text node with the wrapper (icon stays untouched)
        parent.replaceChild(wrapper, textNode);

        // 4. Watch hidden span for translation → re-render split
        const obs = new MutationObserver(() => {
          const newText = hiddenSpan.innerText || hiddenSpan.textContent;
          if (newText && newText !== wrapper.getAttribute('data-last-text')) {
            wrapper.setAttribute('data-last-text', newText);
            wrapper.innerHTML = '';
            const { frag: newFrag } = buildSplitSpans(newText, 0);
            wrapper.appendChild(newFrag);
            requestAnimationFrame(() => {
              wrapper.querySelectorAll('span').forEach((s) => {
                s.style.opacity = '1';
                s.style.transform = 'translateY(0)';
              });
            });
          }
        });
        obs.observe(hiddenSpan, { childList: true, characterData: true, subtree: true });
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;

          if (entry.isIntersecting) {
            const scrollDir = lastScrollY.current > (el._lastScrollY || 0)
              ? 'down'
              : 'up';
            el.setAttribute('data-scroll-dir', scrollDir);
            el.classList.add('visible');
            
            if (el.matches('h1:not(.hero-title), h2, h3:not(.stat-number), h4, h5, h6, .section-title')) {
              elasticTextNodes(el);
            }
          } else {
            const isText = el.matches('h1, h2, h3, h4, h5, h6, p, .section-title, .hero-title, .hero-badge, .section-subtitle');
            if (!isText) {
              el.classList.remove('visible');
              el._lastScrollY = lastScrollY.current;
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    let timer;

    const attach = () => {
      const scope = containerSelector
        ? document.querySelector(containerSelector)
        : document;
      if (!scope) return;
      const els = scope.querySelectorAll('.animate-hidden, h1:not(.hero-title), h2, h3:not(.stat-number), h4, h5, h6, .section-title');
      els.forEach((el) => {
        el._lastScrollY = window.scrollY;
        observer.observe(el);
        
        if (el.matches('h1:not(.hero-title), h2, h3:not(.stat-number), h4, h5, h6, .section-title')) {
          elasticTextNodes(el);
        }
      });
    };

    // Watch for new dynamic elements (like API results)
    const mutationObserver = new MutationObserver(() => {
      attach();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    if (delay > 0) {
      timer = setTimeout(attach, delay);
    } else {
      attach();
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [containerSelector, delay]);
}